import {
    ICellMetric,
    ICellMetricAggregatorCalcLevel,
    ICellMetricNumberType,
    ICellMetricRenderOptions,
    ICubePerspective,
    ICubePerspectiveOptions,
    ICubeSource,
    ICubeSourceChangeEvent,
    IDimensionCompositeKeys,
    IDimensionDistinctKeys,
    IKeyOptions
} from "../PA.HyperRectangle.Core/PA.HyperRectangle.IF";
import {IHaveSimulationCollection} from "../PA.Simulation.Core/PA.Simulation.IF.Aspects";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {BehaviorSubject} from "rxjs";
import {DimensionType, IHierarchy} from "../PA.Core/PA.Core.IF.Hierarchy";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {SimulationComparisonCell} from "./SimulationComparisonCell";
import {Perspective} from "../PA.HyperRectangle.Core/Perspective";
import * as _ from "lodash";

import {
    WB_ModelPredictorRelationshipRow,
    WB_ModelPredictorRow, WB_ModelProfileRow,
    WB_ModelRow,
    WB_SimulationInputRelationshipRow,
    WB_SimulationInputRow,
    WB_SimulationMetricsRow,
    WB_SimulationPredictorRelationshipRow,
    WB_SimulationRow
} from "../PA.Core/PAdbSchemaRows";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Model,
    esWB_Model_Predictor_Relationship, esWB_Model_Profile, esWB_Model_Profile_Group, esWB_Model_Profile_Group_Predictor,
    esWB_Published_Model,
    esWB_Simulation,
    esWB_Simulation_Input_Relationship,
    esWB_Simulation_Metrics,
    esWB_Simulation_Predictor_Relationship
} from "../PA.Core/PAdbSchema";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {SimulationHierarchy} from "../PA.Simulation.Services/SimulationHierarchy";
import {LeafColumnMetric} from "../PA.HyperRectangle.Core/LeafColumnMetric";
import {ComputedMetric} from "../PA.HyperRectangle.Core/ComputedMetric";
import {ProductHierarchy} from "../PA.MD.Services/ProductHierarchy";
import {PeriodHierarchy} from "../PA.MD.Services/PeriodHierarchy";
import {SimulatorPredictorMetric} from "./SimulatorPredictorMetric";
import {SimulatorInputMetric} from "./SimulatorInputMetric";
import {CellMetricUtils} from "../PA.HyperRectangle.Core/CellMetricUtils";
import {CellUtils} from "../PA.HyperRectangle.Core/CellUtils";
import {HierarchyUsage} from "../PA.MD.Hierarchy/HierarchyUsage";
import {WarningMessage_restate} from "../PA.Core/Basic/MessageUtils";

interface ILeafEntry {
    Cell: SimulationComparisonCell,
    Predictor: WB_SimulationPredictorRelationshipRow,
    Input: WB_SimulationInputRelationshipRow,
    Metric: WB_SimulationMetricsRow
}

const arrSimMtFieldNames = [
    'GROSS_LIST_PRICE', 'NET_LIST_PRICE', 'VCM', 'STATCS_FACTOR', 'STD_FUNDING_RATE', 'ADDL_FUNDING_RATE', 'RETAILER_BASE_PRICE', 'RETAILER_PROMO_PRICE',
    'CNS_BASE_UNITS', 'CNS_INCR_UNITS', 'CNS_TOTAL_UNITS',
    'FEATUREONLY_ACV_N', 'DISPLAYONLY_ACV_N', 'FEATUREDISPLAY_ACV_N', 'TPRONLY_ACV_N', 'PROMOTED_ACV_N', 'DIST_ACV_N', 'ACV_D',
    'CNS_FORWARD_BUY_PCNT',
    'SHIP_UNITS', 'SPEND_TOTAL', 'SPEND_FIXED', 'SPEND_CNS_VARIABLE', 'SPEND_SHIP_VARIABLE'
]


export class SimulationCollectionCubeSource
    implements ICubeSource {

    private refContext: PAdbContext
    private dbContext: PAdbContext  // Used for Reference Data
    private _loadMessageProvider: IMessageProvider;
    private _loadProgressProvider: IProgressProvider;
    private _cubeSourceName: string;
    private _onCubeSourcePropertyChange: BehaviorSubject<ICubeSourceChangeEvent>;
    private _onICubeSourceLoadComplete: Promise<void>;
    private _bICubeSourceLoaded: boolean;

    private simulationCollection: IHaveSimulationCollection
    private _qsCubeSource_syncLeafCells: ExecutionSerializer

    private dctHierarchy: { [id: string]: IHierarchy }
    private PromotionHierarchy: IHierarchy
    private ProductHierarchy: IHierarchy
    private TimeHierarchy: IHierarchy
    private dctCellLeafLevel: { [dimType: string]: string }

    private dctMetrics_CalcMt: { [id: string]: ICellMetric }
    private dctMetrics_CalcMt_Hidden: { [id: string]: ICellMetric }
    private dctMetrics_SimInput: { [id: string]: ICellMetric }
    private dctMetrics_Predictor: { [id: string]: ICellMetric }
    private _dctRefreshListByMetric: { [metricId: string]: string[] }

    private arrLeafCells: SimulationComparisonCell[]
    private dctSimulationLoaded: { [wbSimulationId: string]: boolean }
    private dctSimulationInvalidateCache: { [wbSimulationId: string]: boolean }

    private dctSimulation: { [wbSimulationId: string]: WB_SimulationRow }

    private dctLeafCells: { [wbSimulationId: string]: { [wbPrdGrpId: string]: { [wbPeriodId: string]: ILeafEntry } } }


    constructor(options?: { simulationCollection?: IHaveSimulationCollection, dbContext: PAdbContext, cubeSourceName?: string }) {
        const self = this

        this._loadMessageProvider = new MessageProvider()
        this._loadProgressProvider = new ProgressProvider()
        this._cubeSourceName = options?.cubeSourceName || "Simulations"
        this._onCubeSourcePropertyChange = new BehaviorSubject<ICubeSourceChangeEvent>(undefined)
        this._onICubeSourceLoadComplete = undefined
        this._bICubeSourceLoaded = false


        this.simulationCollection = options?.simulationCollection
        this.dbContext = options?.dbContext
        //  if(!this.dbContext)
        this.dbContext = new PAdbContext();
        this.refContext = new PAdbContext()

        this.dctHierarchy = {}
        this.dctCellLeafLevel = {}
        this.PromotionHierarchy = new SimulationHierarchy({simulationCollection: this.simulationCollection})
        this.dctHierarchy[DimensionType.PromotionPlan] = this.PromotionHierarchy
        this.dctCellLeafLevel[DimensionType.PromotionPlan] = "Simulation"

        this.dctSimulation = {}
        this.dctMetrics_CalcMt = {}
        this.dctMetrics_CalcMt_Hidden = {}
        this.dctMetrics_SimInput = {}
        this.dctMetrics_Predictor = {}
        this._dctRefreshListByMetric = {}

        this.dctLeafCells = {}
        this.arrLeafCells = []

        this.dctSimulationLoaded = {}
        this.dctSimulationInvalidateCache = {}

        self.setupSimulationMetrics()

        this._qsCubeSource_syncLeafCells = new ExecutionSerializer(this, this._ICubeSource_syncLeafCells.bind(this),
            [{}], 250, false)

        this.simulationCollection.OnSimulationCollectionChanged.subscribe((aSimulationCollection) => {
            //--
            self.ICubeSource_syncLeafCells()
        })

        self.ICubeSource_syncLeafCells()

    }

    private setupSimulationMetrics(): void {

        // Setup Metrics

        //-- Hidden Metrics
        // Add Leaf Metrics (not visible)
        _.forEach(arrSimMtFieldNames, (aFieldName) => {
            let metricId = "SMT_" + aFieldName
            let mtMetric
                = new LeafColumnMetric(metricId, metricId, aFieldName,
                {Type: "SimulationCalcOutputMetric"},
                {numberType: ICellMetricNumberType.Decimal})
            this.dctMetrics_CalcMt_Hidden[metricId] = mtMetric
        })

        // Create Computed Metrics
        interface MetricInfo {
            id: string,
            name?: string,
            rOps?: ICellMetricRenderOptions,
            formula: string,
            group?: string,
            sort?: number
        }

        const decimal = {rOps: {numberType: ICellMetricNumberType.Decimal}}
        const percent = {rOps: {numberType: ICellMetricNumberType.Percentage}}
        const grpHidden = {group: "Hidden"}
        const grpVolume = {group: "Volume"}
        const grpShipments = {group: "Shipments"}
        const grpMerch = {group: "Merchandising Levels"}
        const grpRetailer = {group: "Retailer Metrics"}
        const grpPrft = {group: "Profitability"}
        const grpSpend = {group: "Spending"}


        //-- Not visible
        let arrComponentFoundationMetrics: MetricInfo[] = [
            {id: "RetailLift_N", formula: "SMT_CNS_INCR_UNITS", ...decimal},
            {id: "RetailLift_D", formula: "SMT_CNS_BASE_UNITS", ...decimal},
            {id: "Cns_Total_BasePriceSls", formula: "SMT_RETAILER_BASE_PRICE * SMT_CNS_TOTAL_UNITS", ...decimal},
            {
                id: "Shp_Total_GrsLstSls",
                name: "Shipment Gross List Sales",
                formula: "SMT_GROSS_LIST_PRICE * SMT_SHIP_UNITS", ...decimal, ...grpHidden
            },
            {
                id: "Cns_Total_LstSls",
                name: "Net List Sales (Cns)",
                formula: "SMT_NET_LIST_PRICE * SMT_CNS_TOTAL_UNITS", ...decimal, ...grpHidden
            },
            {
                id: "Cns_Base_PrftCntrb",
                name: "Base Variable Contribution",
                formula: "SMT_VCM * SMT_CNS_BASE_UNITS", ...decimal, ...grpHidden
            },

        ]

        for (let info of arrComponentFoundationMetrics) {
            let metric = new ComputedMetric(info.id, info.name || info.id, info.formula, ICellMetricAggregatorCalcLevel.Leaf, {}, info.rOps, true)
            this.dctMetrics_CalcMt_Hidden[metric.MetricId] = metric
        }


        //-- Additive Metrics
        let arrAdditiveMetrics: MetricInfo[] = [

            {
                id: "Shp_Total_LstSls",
                name: "Net List Sales (Ship)",
                formula: "SMT_NET_LIST_PRICE * SMT_SHIP_UNITS", ...decimal, ...grpShipments,
                sort: 41
            },
            {
                id: "Shp_Net_Revenue",
                name: "Net Revenue (Ship)",
                formula: "(SMT_NET_LIST_PRICE * SMT_SHIP_UNITS) - SMT_SPEND_TOTAL", ...decimal, ...grpShipments,
                sort: 42
            },

            {
                id: "Cns_Base_Units",
                name: "Base Retail Units",
                formula: "SMT_CNS_BASE_UNITS", ...decimal, ...grpVolume,
                sort: 11
            },
            {
                id: "Cns_Incr_Units",
                name: "Marginal Incr. Retail Units",
                formula: "SMT_CNS_INCR_UNITS", ...decimal, ...grpVolume,
                sort: 12
            },
            {
                id: "Cns_Total_Units",
                name: "Promoted Retail Units",
                formula: "SMT_CNS_TOTAL_UNITS", ...decimal, ...grpVolume,
                sort: 13
            },
            {
                id: "Shp_Total_Units",
                name: "Shipment Units",
                formula: "SMT_SHIP_UNITS", ...decimal, ...grpVolume,
                sort: 10
            },

            {
                id: "Cns_Base_RtlSls",
                name: "Base Retail Sales",
                formula: "SMT_RETAILER_PROMO_PRICE * SMT_CNS_BASE_UNITS", ...decimal, ...grpRetailer,
                sort: 71
            },
            {
                id: "Cns_Total_RtlSls",
                name: "Retail Sales",
                formula: "SMT_RETAILER_PROMO_PRICE * SMT_CNS_TOTAL_UNITS", ...decimal, ...grpHidden
            },
            {
                id: "Cns_Incr_RtlSls",
                name: "Marginal Incr. Retail Sales",
                formula: "SMT_CNS_INCR_UNITS * SMT_RETAILER_PROMO_PRICE", ...decimal, ...grpRetailer,
                sort: 72
            },
            {
                id: "Cns_Total_PrftCntrb",
                name: "Variable Contribution (Cnsmp)",
                formula: "(SMT_VCM * SMT_CNS_TOTAL_UNITS) - SMT_SPEND_TOTAL", ...decimal, ...grpPrft,
                sort: 6
            },
            {
                id: "Shp_Total_PrftCntrb",
                name: "Variable Contribution (Ship)",
                formula: "(SMT_VCM * SMT_SHIP_UNITS) - SMT_SPEND_TOTAL", ...decimal, ...grpPrft,
                sort: 7
            },
            {
                id: "Cns_Incr_PrftCntrb",
                name: "Marginal Incr. Contribution (Cnsmp)",
                formula: "(SMT_VCM * SMT_CNS_INCR_UNITS) - SMT_SPEND_TOTAL", ...decimal, ...grpPrft,
                sort: 8
            },

            {
                id: "Retailer_Margin",
                name: "Retailer Margin",
                formula: "( SMT_RETAILER_PROMO_PRICE - SMT_NET_LIST_PRICE ) * SMT_CNS_TOTAL_UNITS + SMT_SPEND_TOTAL ", ...decimal, ...grpRetailer,
                sort: 73
            },
            {id: "Total_Spend", name: "Trade Spend", formula: "SMT_SPEND_TOTAL", ...decimal, ...grpSpend, sort: 31},
            {
                id: "Variable_Spend",
                name: "Variable Spend",
                formula: "SMT_SPEND_CNS_VARIABLE + SMT_SPEND_SHIP_VARIABLE", ...decimal, ...grpSpend,
                sort: 32
            },
            {id: "Fixed_Spend", name: "Fixed Spend", formula: "SMT_SPEND_FIXED", ...decimal, ...grpSpend, sort: 33},
            {id: "Feature_ACV_N", name: "Feature ACV N", formula: "SMT_FEATUREONLY_ACV_N", ...decimal, ...grpHidden},
            {id: "Display_ACV_N", name: "Display ACV N", formula: "SMT_DISPLAYONLY_ACV_N", ...decimal, ...grpHidden},
            {id: "FD_AVC_N", name: "F&D AVC N", formula: "SMT_FEATUREDISPLAY_ACV_N", ...decimal, ...grpHidden},
            {id: "TPR_ACV_N", name: "TPR ACV N", formula: "SMT_TPRONLY_ACV_N", ...decimal, ...grpHidden},
            {id: "ACV_D", name: "ACV D", formula: "SMT_ACV_D", ...decimal, ...grpHidden},


        ]

        for (let info of arrAdditiveMetrics) {
            let bHidden = info.group == grpHidden.group
            let metric = new ComputedMetric(info.id, info.name, info.formula, ICellMetricAggregatorCalcLevel.Leaf, {sort: info.sort}, info.rOps, bHidden, info.group)
            if (bHidden)
                this.dctMetrics_CalcMt_Hidden[metric.MetricId] = metric
            else
                this.dctMetrics_CalcMt[metric.MetricId] = metric
        }


        // Compound Metrics
        let arrCompoundMetrics: MetricInfo[] = [
            {
                id: "Lift_Pcnt",
                name: "Marginal Retail Lift",
                formula: "RetailLift_N / RetailLift_D", ...percent, ...grpVolume,
                sort: 14
            },
            {
                id: "TS_Ratio",
                name: "T:S Ratio",
                formula: "Total_Spend / Cns_Total_LstSls", ...percent, ...grpSpend,
                sort: 34
            },
            {
                id: "Cost_Per_Incr_Unit",
                name: "Cost Per Marginal Incr. Unit",
                formula: "Total_Spend / Cns_Incr_Units", ...decimal, ...grpSpend,
                sort: 35
            },
            {
                id: "Retailer_Margin_Pcnt",
                name: "Retailer Margin %",
                formula: "Retailer_Margin / Cns_Total_RtlSls", ...percent, ...grpRetailer,
                sort: 74
            },
            {
                id: "Retailer_Margin_wo_Fixed_Pcnt",
                name: "Retailer Margin (w/o Fixed Cost) %",
                formula: "(Retailer_Margin - Fixed_Spend) / Cns_Total_RtlSls", ...percent, ...grpRetailer,
                sort: 75
            },
            {
                id: "Retailer_Discount_Pcnt",
                name: "Shelf Price Discount %",
                formula: "(Cns_Total_BasePriceSls - Cns_Total_RtlSls) / Cns_Total_BasePriceSls", ...percent, ...grpMerch,
                sort: 61
            },
            {
                id: "Retailer_Shelf_Price",
                name: "Shelf Price",
                formula: "Cns_Total_RtlSls / Cns_Total_Units", ...decimal, ...grpHidden
            },
            {
                id: "Retailer_Base_Price",
                name: "Base Price",
                formula: "Cns_Total_BasePriceSls / Cns_Total_Units", ...decimal, ...grpHidden
            },
            {id: "ROI_Pcnt", name: "ROI", formula: "Cns_Incr_PrftCntrb / Total_Spend", ...percent, ...grpPrft, sort: 9},
            {
                id: "Display_ACV",
                name: "% ACV D (W/Avg)",
                formula: "Display_ACV_N/ACV_D/100", ...percent, ...grpMerch,
                sort: 62
            },
            {
                id: "Feature_ACV",
                name: "% ACV F (W/Avg)",
                formula: "Feature_ACV_N/ACV_D/100", ...percent, ...grpMerch,
                sort: 63
            },
            {
                id: "FeatureDisplay_ACV",
                name: "% ACV F&D (W/Avg)",
                formula: "FD_AVC_N/ACV_D/100", ...percent, ...grpMerch,
                sort: 64
            },
            {
                id: "TPR_ACV",
                name: "% ACV TPR (W/Avg)",
                formula: "TPR_ACV_N/ACV_D/100", ...percent, ...grpMerch,
                sort: 65
            }
        ]

        for (let info of arrCompoundMetrics) {
            let bHidden = info.group == grpHidden.group
            let metric = new ComputedMetric(info.id, info.name, info.formula, ICellMetricAggregatorCalcLevel.Perspective, {sort: info.sort}, info.rOps, false, info.group,)
            if (bHidden)
                this.dctMetrics_CalcMt_Hidden[metric.MetricId] = metric
            else
                this.dctMetrics_CalcMt[metric.MetricId] = metric
        }

        this._dctRefreshListByMetric = CellMetricUtils.getRefreshMetricParentsByMetricId(this)


    }

    get DefaultMetricList(): string[] {
        let returnValue: string[] = []
        returnValue.push("Total_Spend")
        let predMetrics = _.sortBy(_.values(this.dctMetrics_Predictor), (a) => a.Sort)
        _.forEach(predMetrics, (aMtrc) => {
            returnValue.push(aMtrc.MetricId)
        })
        let computedMetrics = _.sortBy(_.values(this.dctMetrics_CalcMt), (a) => a.Sort)
        _.forEach(computedMetrics, (aMtrc) => {
            returnValue.push(aMtrc.MetricId)
        })
        return returnValue
    }

    get CubeSourceName(): string {
        return this._cubeSourceName
    }

    get CubeSourceLoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get CubeSourceLoadProgressProvider(): IProgressProvider {
        return this._loadProgressProvider
    }

    get OnCubeSourcePropertyChange(): BehaviorSubject<ICubeSourceChangeEvent> {
        return this._onCubeSourcePropertyChange
    }

    get bICubeSourceLoaded(): boolean {
        return this._bICubeSourceLoaded
    };

    get OnICubeSourceLoadComplete(): Promise<void> {
        return this._onICubeSourceLoadComplete
    }


    private get CubeSourceLeafCells(): SimulationComparisonCell[] {
        return this.arrLeafCells
    }

    createPerspective(name: string, options: ICubePerspectiveOptions): ICubePerspective {
        const self = this

        const cellFunction = async () => {
            return CellUtils.getFilterScope(self, options, self.CubeSourceLeafCells, self.dctCellLeafLevel)
        };

        return new Perspective(name, this, options, {cells: cellFunction});
    }

    async getDistinctLeafKeys(dimensionTypes: DimensionType[], options: IKeyOptions): Promise<IDimensionDistinctKeys> {
        return CellUtils.getDistinctKeys(dimensionTypes, this.CubeSourceLeafCells)
    }

    async getCompositeLeafKeys(dimensionTypeIds: DimensionType[], options: IKeyOptions): Promise<IDimensionCompositeKeys> {
        return CellUtils.getCompositeKeys(dimensionTypeIds, this.CubeSourceLeafCells, false)
    }

    invalidateCache(wbSimulationId: number) {
        this.dctSimulationInvalidateCache[wbSimulationId] = true
        this.ICubeSource_syncLeafCells()
    }

    ICubeSource_syncLeafCells(): void {
        //  debounce calls ...
        console.log("Synchronizing Cube Source Leaf Cells: qsRun")
        this._qsCubeSource_syncLeafCells.Run({})
    }

    private async _ICubeSource_syncLeafCells(opts: any): Promise<void> {
        const self = this
        self.CubeSourceLoadProgressProvider.start("Loading")
        self.CubeSourceLoadProgressProvider.setProgressMessage("Dimension-Metrics", "Updating Dimensions")
        await self.updateDimensionsAndMetrics()

        self.CubeSourceLoadProgressProvider.setProgressMessage("RowData", "Updating Metric Data")

        // Invalidate anything, reset
        _.forEach(_.keys(self.dctSimulationInvalidateCache), (wbSimulationId: string) => {
            delete self.dctLeafCells[wbSimulationId]
            delete self.dctSimulationLoaded[wbSimulationId]
            self.dbContext.purgeCache(esWB_Simulation_Input_Relationship, [])
            self.dbContext.purgeCache(esWB_Simulation_Predictor_Relationship, [])
            self.dbContext.purgeCache(esWB_Simulation_Metrics, [])

        })
        self.dctSimulationInvalidateCache = {}

        let arrSimulationIdLoaded: number[] = _.map(_.keys(self.dctSimulationLoaded), (a) => _.toNumber(a))
        let arrSimulationsInScope = _.map(self.simulationCollection.Simulations, (s: WB_SimulationRow) => s.cif.WB_SIMULATION_ID)


        //-- Remove items not in scope
        const arrSimulationIdToRemove: number[] = _.difference(arrSimulationIdLoaded, arrSimulationsInScope)
        _.forEach(arrSimulationIdToRemove, (wbSimulationId: any) => {
            delete self.dctLeafCells[wbSimulationId]
            delete self.dctSimulationLoaded[wbSimulationId]
            self.dbContext.purgeCache(esWB_Simulation_Input_Relationship, [])
            self.dbContext.purgeCache(esWB_Simulation_Predictor_Relationship, [])
            self.dbContext.purgeCache(esWB_Simulation_Metrics, [])
        })

        const arrSimulationIdToAdd: number[] = _.difference(arrSimulationsInScope, arrSimulationIdLoaded)

        if (!_.isEmpty(arrSimulationIdToAdd)) {

            const rr = new ESRecordResolver(self.dbContext, {
                standardConditions: {
                    [esWB_Simulation_Input_Relationship]: {DELETED_IND: {neq: '1'}},
                    [esWB_Simulation_Predictor_Relationship]: {DELETED_IND: {neq: '1'}},
                    [esWB_Simulation_Metrics]: {DELETED_IND: {neq: '1'}}
                }
            })

            const inQuery: any = {WB_SIMULATION_ID: {inq: arrSimulationIdToAdd}, DELETED_IND: {neq: '1'}}

            const arrSimPredictors: WB_SimulationPredictorRelationshipRow[] = <WB_SimulationPredictorRelationshipRow[]>await rr.queryAllWithRelationships(esWB_Simulation_Predictor_Relationship, inQuery)
            const arrSimInputs: WB_SimulationInputRelationshipRow[] = <WB_SimulationInputRelationshipRow[]>await rr.queryAllWithRelationships(esWB_Simulation_Input_Relationship, inQuery)
            const arrSimMetrics: WB_SimulationMetricsRow[] = <WB_SimulationMetricsRow[]>await rr.queryAllWithRelationships(esWB_Simulation_Metrics, inQuery)

            _.forEach(arrSimPredictors, (aRow: WB_SimulationPredictorRelationshipRow) => {
                const entry = self.createOrFindLeaf(aRow.cif.WB_SIMULATION_ID, aRow.cif.WB_PRDGRP_ID, aRow.cif.WB_PERIOD_ID)
                entry.Predictor = aRow
            })

            _.forEach(arrSimInputs, (aRow: WB_SimulationInputRelationshipRow) => {
                let entry = self.createOrFindLeaf(aRow.cif.WB_SIMULATION_ID, aRow.cif.WB_PRDGRP_ID, aRow.cif.WB_PERIOD_ID)
                entry.Input = aRow
            })

            _.forEach(arrSimMetrics, (aRow: WB_SimulationMetricsRow) => {
                let entry = self.createOrFindLeaf(aRow.cif.WB_SIMULATION_ID, aRow.cif.WB_PRDGRP_ID, aRow.cif.WB_PERIOD_ID)
                entry.Metric = aRow
            })

            _.forEach(arrSimulationIdToAdd, (wbSimulationId) => {
                self.dctSimulationLoaded[wbSimulationId] = true
            })


        }

        //-- Repopulate the Leaf Array
        self.arrLeafCells = []
        self.forEachLeafEntry((wbSimulationId, wbPrdGrpId, wbPeriodId, entry) => {
            if (!entry.Cell)
                entry.Cell = new SimulationComparisonCell(self, entry.Predictor, entry.Input, entry.Metric)
            else {
                if (!entry.Cell.rowsMatch(entry.Predictor, entry.Input, entry.Metric))
                    entry.Cell = new SimulationComparisonCell(self, entry.Predictor, entry.Input, entry.Metric)
            }

            self.arrLeafCells.push(entry.Cell)
        })

        self.CubeSourceLoadProgressProvider.finish()

        self.OnCubeSourcePropertyChange.next({Source: self, PropertyName: "Perspective", PropertyValue: undefined})
    }

    private createOrFindLeaf(wbSimulationId: number | string, wbPrdGrpId: number | string, wbPeriodId: number | string): ILeafEntry {

        if (!(wbSimulationId in this.dctLeafCells))
            this.dctLeafCells[wbSimulationId] = {}
        let simLevel = this.dctLeafCells[wbSimulationId]

        if (!(wbPrdGrpId in simLevel))
            simLevel[wbPrdGrpId] = {}

        let prdGrpLevel = simLevel[wbPrdGrpId]

        if (!(wbPeriodId in prdGrpLevel)) {
            let newLeafEntry: ILeafEntry = {
                Cell: undefined,
                Input: undefined,
                Predictor: undefined,
                Metric: undefined
            }

            prdGrpLevel[wbPeriodId] = newLeafEntry
        }

        return prdGrpLevel[wbPeriodId]

    }

    private forEachLeafEntry(func: (wbSimulationId: number | string, wbPrdGrpId: number | string, wbPeriodId: number | string, entry: ILeafEntry) => void) {
        _.forEach(this.dctLeafCells, (subDict, wbSimulationId) => {
            _.forEach(subDict, (subDict2, wbPrdGrpId) => {
                _.forEach(subDict2, (value, wbPeriodId) => {
                    func(wbSimulationId, wbPrdGrpId, wbPeriodId, value)
                })
            })
        })
    }

    // This is only called from the Sync function, should not be called separately
    private async updateDimensionsAndMetrics(): Promise<void> {
        const self = this

        let arrSimulationsInScope = _.map(self.simulationCollection.Simulations, (s: WB_SimulationRow) => s.cif.WB_SIMULATION_ID)

        let arrSimulationsToLoad: number[] = []
        _.forEach(arrSimulationsInScope, (wbSimulationId) => {
            if (!(wbSimulationId in self.dctSimulation))
                arrSimulationsToLoad.push(wbSimulationId)
        })

        if (!_.isEmpty(arrSimulationsToLoad)) {

            let rr = new ESRecordResolver(self.refContext, {
                standardConditions: {
                    [esWB_Simulation]: {DELETED_IND: {neq: '1'}},
                },
                dctExcludeHasManyNamedRelations: {
                    [esWB_Simulation]: ['All'],
                    [esWB_Model]: ['All']
                },
                dctIncludeBelongsToNamedRelations: {
                    [esWB_Published_Model]: [esWB_Model],
                    [esWB_Model_Profile]: [esWB_Model_Profile_Group, esWB_Model_Profile_Group_Predictor]
                }
            })

            let arrSimulations: WB_SimulationRow[] = <WB_SimulationRow[]>await rr.findAllWithRelationships(esWB_Simulation, arrSimulationsToLoad)

            //-- Add to Dictionary
            let dctModelKeys: { [id: number]: number } = {}
            _.forEach(arrSimulations, (aRow: WB_SimulationRow) => {
                self.dctSimulation[aRow.cif.WB_SIMULATION_ID] = aRow
                let wbProfileId = aRow.cif.WB_MODEL_PROFILE_ID
                if (_.isNumber(wbProfileId) && (aRow?.WB_ModelProfileRow?.WB_ModelProfileGroupRows?.length == 0))
                    dctModelKeys[wbProfileId] = wbProfileId
            })

            if (!_.isEmpty(dctModelKeys)) {
                let arrWBProfileGroupId = _.values(dctModelKeys)
                await rr.queryAllWithRelationships(esWB_Model_Profile_Group, {WB_MODEL_PROFILEGR_ID: {inq: arrWBProfileGroupId}})
            }

        } // Loaded


        //  Determine the distinct Models, Scenario Types
        let firstModelProfile: WB_ModelProfileRow = undefined
        let firstSimulation: WB_SimulationRow = undefined
        const dctModels: { [wbModelProfileId: string]: WB_ModelProfileRow } = {}

        _.forEach(arrSimulationsInScope, (wbSimulationId) => {
            if (!(wbSimulationId in self.dctSimulation)) {
                console.log("Unknown simulation: ", wbSimulationId)
                return
            }

            let simulation = self.dctSimulation[wbSimulationId]

            if (!simulation)
                return

            if (!firstSimulation)
                firstSimulation = simulation

            const modelProfile = simulation.WB_ModelProfileRow
            if (modelProfile) {
                if (!firstModelProfile)
                    firstModelProfile = modelProfile
                dctModels[modelProfile.cif.WB_MODEL_PROFILE_ID] = modelProfile
            }


        })

        // Update the Hierarchies as needed..
        if (_.isEmpty(firstSimulation) || _.isEmpty(firstModelProfile)) {
            this.dctMetrics_SimInput = {}
            this.dctMetrics_Predictor = {}
            return
        }

        const effStartDate = firstSimulation.StartDate
        const effEndDate = firstSimulation.EndDate

        const wbPrdHId = firstSimulation.cif?.WB_PRDH_ID + ''
        if ((!self.ProductHierarchy) || (self.ProductHierarchy?.HierarchyId != wbPrdHId)) {
            this.ProductHierarchy = new ProductHierarchy(self.simulationCollection.ProductHierarchy?.cif?.WB_PRDH_ID, effStartDate, effEndDate, {bExcludeProducts: true})
            await this.ProductHierarchy.OnLoadComplete
            self.dctHierarchy[DimensionType.Product] = self.ProductHierarchy
            const hs = new HierarchyUsage()
            const leafLevel = await hs.getSrcEntityLevelId("Product",
                self.simulationCollection.ProductHierarchy?.cif?.WB_PRDH_ID,
                self.simulationCollection.ProductEntity?.cif?.WB_ENTITY_ID)

            WarningMessage_restate(self.CubeSourceLoadMessageProvider, _.isEmpty(leafLevel), "PrdEntityLevelNotFound",
                `Product Hierarchy Entity Level ${self.simulationCollection.ProductEntity?.cif?.ENTITY_NAME} not found`)
            self.dctCellLeafLevel[DimensionType.Product] = leafLevel
        }

        const wbPeriodHId = self.simulationCollection.PeriodHierarchy?.cif?.c + ''
        if ((!self.TimeHierarchy) || (self.TimeHierarchy?.HierarchyId != wbPeriodHId)) {
            self.TimeHierarchy = new PeriodHierarchy(self.simulationCollection.PeriodHierarchy?.cif?.WB_PERIODH_ID, {})
            await self.TimeHierarchy.OnLoadComplete
            self.dctHierarchy[DimensionType.Time] = self.TimeHierarchy
            const hs = new HierarchyUsage()
            const leafLevel = await hs.getSrcEntityLevelId("Period",
                self.simulationCollection.PeriodHierarchy?.cif?.WB_PERIODH_ID,
                self.simulationCollection.PeriodEntity?.cif?.WB_ENTITY_ID)
            WarningMessage_restate(self.CubeSourceLoadMessageProvider, _.isEmpty(leafLevel), "PeriodEntityLevelNotFound",
                `Period Hierarchy Entity Level ${self.simulationCollection.PeriodEntity?.cif?.ENTITY_NAME} not found`)
            self.dctCellLeafLevel[DimensionType.Time] = leafLevel
        }

        const dctPredictors: { [id: number]: WB_ModelPredictorRow } = {}
        const dctInputs: { [id: number]: WB_SimulationInputRow } = {}

        //-- Iterate over Models to get distinct Predictors
        _.forEach(dctModels, (model, wbModelId) => {
            _.forEach(model?.WB_ModelProfileGroupRows, group => {
                if (!group.isActive)
                    return
                _.forEach(group.WB_ModelProfileGroupPredictorRows, aRow => {
                    if (!aRow.isActive)
                        return

                    let p = aRow.WB_ModelPredictorRow
                    if (p)
                        dctPredictors[p.cif.WB_MODEL_PREDICTOR_ID] = p
                })
            })
        })

        let dctMetrics_replace: { [id: string]: ICellMetric } = {}
        //-- Add Missing Predictors & Replace
        _.forEach(dctPredictors, (value, key) => {
            let id_ui = value.cif.MODEL_PREDICTOR_ID
            if (_.isEmpty(self.dctMetrics_Predictor[id_ui]))
                dctMetrics_replace[id_ui] = new SimulatorPredictorMetric(value, true)
            else
                dctMetrics_replace[id_ui] = self.dctMetrics_Predictor[id_ui]
        })
        self.dctMetrics_Predictor = dctMetrics_replace

        dctMetrics_replace = {}
        //-- Add Missing Inputs & Replace
        _.forEach(dctInputs, (value, key) => {
            const id_ui = value.cif.SIMULATION_INPUT_ID
            if (_.isEmpty(self.dctMetrics_SimInput[id_ui]))
                dctMetrics_replace[id_ui] = new SimulatorInputMetric(value, true)
            else
                dctMetrics_replace[id_ui] = self.dctMetrics_SimInput[id_ui]
        })
        self.dctMetrics_SimInput = dctMetrics_replace

        self._bICubeSourceLoaded = true
    }

    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy {
        return this.dctHierarchy[dimensionType];
    }

    getDimensions(): DimensionType[] {
        return [DimensionType.PromotionPlan, DimensionType.Product, DimensionType.Time];
    }

    getMetric(metricId: string): ICellMetric {

        if (metricId in this.dctMetrics_CalcMt)
            return this.dctMetrics_CalcMt[metricId];

        if (metricId in this.dctMetrics_CalcMt_Hidden)
            return this.dctMetrics_CalcMt_Hidden[metricId];

        if (metricId in this.dctMetrics_Predictor)
            return this.dctMetrics_Predictor[metricId];

        if (metricId in this.dctMetrics_SimInput)
            return this.dctMetrics_SimInput[metricId];

    }

    getMetrics(): ICellMetric[] {
        // Only return Visible Metrics
        return _.union(_.values(this.dctMetrics_CalcMt), _.values(this.dctMetrics_Predictor), _.values(this.dctMetrics_SimInput))
    }

    getRefreshMetricIds(aMetricId: string): string[] {
        return (this._dctRefreshListByMetric[aMetricId] || [aMetricId]);
    }


}